import React, { useState, useEffect, useCallback, useRef } from "react";
import { Visit } from "../../../interfaces/Visit";
import { updateVisitEntry, createVisitEntry } from "../../../apis/visit";
import { useAppDispatch } from "../../../hooks";
import { changeVisit } from "../../../features/visit/visitSlice";
import { useNotification } from "../../../NotificationContext";
import { format } from "date-fns";
import Modal from "../../../components/Modal";
import AssessmentForm from "./forms/AssessmentForm";
import DoctorRoundsForm from "./forms/DoctorRoundsForm";
import NursingForm from "./forms/NursingForm";
import PharmacyRequestForm from "./forms/PharmacyRequestForm";
import LabRequestForm from "./forms/LabRequestForm";
import RoomTransferForm from "./forms/RoomTransferForm";
import PaymentEntryForm from "./forms/PaymentEntryForm";
import IPDCustomForm from "./IPDCustomForm";
import { getIPDLayout, hasCustomIPDLayout, IPDEntryType } from "../../../utils/IPDLayoutUtil";

interface IPDEntry {
  id?: string;
  type: string;
  date: string;
  data: any;
  userId?: number;
}

interface IPDEntryFormProps {
  visit: Visit;
  entryType: string;
  entry?: IPDEntry;
  onSave: () => void;
  onCancel: () => void;
}

const IPDEntryForm: React.FC<IPDEntryFormProps> = ({
  visit,
  entryType,
  entry,
  onSave,
  onCancel,
}) => {
  const dispatch = useAppDispatch();
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState<any>({});
  const [saving, setSaving] = useState(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    if (entry) {
      setFormData(entry.data || {});
    } else {
      setFormData({});
    }
  }, [entry]);

  const handleSave = useCallback(async (data: any) => {
    // Prevent multiple submissions using ref (immediate check)
    if (isSubmittingRef.current) {
      console.log("Prevented duplicate submission - already submitting");
      return;
    }

    try {
      isSubmittingRef.current = true;
      setSaving(true);
      const user = JSON.parse(window.localStorage.getItem("user") || "{}");
      const flowTypeId = visit.meta?.ipdFlowTypeId || visit.clinicVisitTypeId || 1;
      
      // For flexible entries (assessment, doctor_rounds, nursing, general_comment, admission, discharge)
      // we need to get the layout ID if it exists
      let clinicVisitTypeLayoutId = null;
      const flexibleEntryTypes = ["assessment", "doctor_rounds", "nursing", "general_comment", "admission", "discharge"];
      
      if (flexibleEntryTypes.includes(entryType)) {
        // Try to get layout ID from visit or layout configuration
        // The backend should handle finding the correct layout based on entry_type and clinic_visit_type_id
        clinicVisitTypeLayoutId = visit.clinicVisitTypeLayoutId || null;
      }

      // Prepare form_data based on entry type
      let formDataToSave = data;
      
      // For fixed entries (pharmacy_request, lab_request, room_transfer, payment)
      // the data should already contain just the type and id
      if (entryType === "pharmacy_request") {
        formDataToSave = {
          pharmacy_sale_id: data.pharmacy_sale_id || data.saleId,
          notes: data.notes || "",
        };
      } else if (entryType === "lab_request") {
        formDataToSave = {
          lab_order_id: data.lab_order_id || data.labOrderId || data.orderId,
          notes: data.notes || "",
        };
      } else if (entryType === "room_transfer") {
        formDataToSave = {
          from_room_id: data.fromRoomId || data.from_room_id || visit.roomId,
          to_room_id: data.toRoomId || data.to_room_id,
          transfer_date: data.transferDate || data.transfer_date || new Date().toISOString(),
          reason: data.reason || "",
          notes: data.notes || "",
        };
      } else if (entryType === "payment") {
        formDataToSave = {
          visit_transaction_id: data.visit_transaction_id || data.visitTransactionId || null,
          patient_transaction_id: data.patient_transaction_id || data.patientTransactionId || null,
          amount: data.amount || 0,
          payment_mode: data.payment_mode || data.paymentMode || "cash",
          notes: data.notes || "",
        };
      }

      // pharmacy_request and lab_request entries are created automatically by the backend
      // when the pharmacy sale or lab order is created, so we don't need to create them here
      const autoCreatedEntryTypes = ["pharmacy_request", "lab_request"];
      
      if (autoCreatedEntryTypes.includes(entryType)) {
        // For pharmacy_request and lab_request, the entry is created automatically by the backend
        // Just show success and refresh the timeline
        showNotification(
          "success",
          `${getEntryTitle()} created successfully`,
          "",
          "topRight"
        );
        onSave();
      } else if (entry?.id) {
        // Update existing entry - entry.id should be a number from the API
        const entryId = typeof entry.id === 'string' ? parseInt(entry.id) : entry.id;
        await updateVisitEntry(visit.id!, entryId, {
          entry_type: entryType,
          clinic_visit_type_layout_id: clinicVisitTypeLayoutId,
          form_data: formDataToSave,
        });
        showNotification(
          "success",
          `${getEntryTitle()} saved successfully`,
          "",
          "topRight"
        );
        onSave();
      } else {
        // Create new entry
        if (!visit.id) {
          throw new Error("Visit ID is required to create an entry");
        }
        
        await createVisitEntry(visit.id, {
          entry_type: entryType,
          clinic_visit_type_layout_id: clinicVisitTypeLayoutId,
          form_data: formDataToSave,
        });
        
        showNotification(
          "success",
          `${getEntryTitle()} saved successfully`,
          "",
          "topRight"
        );
        onSave();
      }
    } catch (error: any) {
      console.error("Failed to save entry:", error);
      const errorMessage = error?.response?.data?.message || `Failed to save ${getEntryTitle()}`;
      showNotification(
        "error",
        errorMessage,
        "",
        "topRight"
      );
    } finally {
      isSubmittingRef.current = false;
      setSaving(false);
    }
  }, [entryType, entry, visit, onSave, showNotification]);

  const getEntryTitle = () => {
    const titles: { [key: string]: string } = {
      assessment: "Assessment",
      doctor_rounds: "Doctor Rounds",
      nursing: "Nursing",
      pharmacy_request: "Pharmacy Request",
      lab_request: "Lab Request",
      room_transfer: "Room Transfer",
      payment: "Payment",
    };
    return titles[entryType] || entryType;
  };

  const renderForm = () => {
    // Get IPD flow type from visit meta or use clinicVisitTypeId (since IPD visit types are the flow types)
    const flowTypeId = visit.meta?.ipdFlowTypeId || visit.clinicVisitTypeId || 1;
    const entryTypeKey = entryType as IPDEntryType;
    console.log(hasCustomIPDLayout(flowTypeId, entryTypeKey));
    // Check if custom layout exists for assessment, doctor_rounds, or nursing
    if (
      (entryType === "assessment" ||
        entryType === "doctor_rounds" ||
        entryType === "nursing") &&
      hasCustomIPDLayout(flowTypeId, entryTypeKey)
    ) {
      const customLayout = getIPDLayout(flowTypeId, entryTypeKey);
      console.log(`Rendering custom layout for flow ${flowTypeId}, entry ${entryType}:`, customLayout);
      if (customLayout && customLayout.gridItems && customLayout.gridItems.length > 0) {
        return (
          <div className="p-4">
            <IPDCustomForm
              gridItems={customLayout.gridItems}
              layout={customLayout.layout}
              visit={visit}
              initialData={formData}
              onDataChange={(data) => {
                setFormData(data);
              }}
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleSave(formData)}
                disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? "Saving..." : `Save ${getEntryTitle()}`}
              </button>
            </div>
          </div>
        );
      } else {
        // Custom layout exists but is empty, fall back to default
        console.warn(`Custom layout for ${entryType} exists but is empty, using default form`);
      }
    }

    // Fall back to default forms
    switch (entryType) {
      case "assessment":
        return (
          <AssessmentForm
            initialData={formData}
            onSave={handleSave}
            onCancel={onCancel}
          />
        );
      case "doctor_rounds":
        return (
          <DoctorRoundsForm
            initialData={formData}
            onSave={handleSave}
            onCancel={onCancel}
          />
        );
      case "nursing":
        return (
          <NursingForm
            initialData={formData}
            onSave={handleSave}
            onCancel={onCancel}
          />
        );
      case "pharmacy_request":
        return (
          <PharmacyRequestForm
            initialData={formData}
            visit={visit}
            onSave={handleSave}
            onCancel={onCancel}
          />
        );
      case "lab_request":
        return (
          <LabRequestForm
            initialData={formData}
            onSave={handleSave}
            onCancel={onCancel}
          />
        );
      case "room_transfer":
        return (
          <RoomTransferForm
            visit={visit}
            initialData={formData}
            onSave={handleSave}
            onCancel={onCancel}
          />
        );
      case "payment":
        return (
          <PaymentEntryForm
            initialData={formData}
            onSave={handleSave}
            onCancel={onCancel}
          />
        );
      default:
        return <div>Unknown entry type</div>;
    }
  };

  const formContent = (() => {
    try {
      return renderForm();
    } catch (error) {
      console.error("Error rendering IPD entry form:", error);
      return (
        <div className="p-4">
          <p className="text-red-500">Error loading form. Please try again.</p>
          <button
            onClick={onCancel}
            className="px-4 py-2 mt-4 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700"
          >
            Close
          </button>
        </div>
      );
    }
  })();

  return (
    <Modal
      width="max-w-4xl"
      title={entry ? `Edit ${getEntryTitle()}` : `Add ${getEntryTitle()}`}
      onClose={onCancel}
      modalContent={formContent}
    />
  );
};

export default IPDEntryForm;

